import { TIME_RANGE_DAYS } from "@repo/adapter-core/date-window";
import {
  getDefaultAdapterConfig as getSharedAdapterDefaultConfig,
  hasAdapter as hasSharedAdapter,
} from "@repo/adapter-definitions";
import type { DomainAdapterConfig } from "@repo/adapter-definitions/domain-com-au";
import { DOMAIN_DEFAULT_CONFIG } from "@repo/adapter-definitions/domain-com-au";
import { DomainConfigPanel } from "@repo/adapter-ui/domain-com-au";
import type { SuburbList } from "@repo/types/suburb-list";
import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import { Checkbox } from "@repo/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/dialog";
import { Input } from "@repo/ui/input";
import { Label } from "@repo/ui/label";
import { RadioGroup, RadioGroupItem } from "@repo/ui/radio-group";
import { ScrollArea } from "@repo/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/ui/select";
import { Switch } from "@repo/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/tooltip";
import {
  Calendar,
  ChevronRight,
  Clock,
  HelpCircle,
  Loader2,
  MapPin,
  Play,
  Plug,
  Search,
  SlidersHorizontal,
  Sparkles,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { AdapterRuntimeConfig } from "@/components/jobs/AdapterRuntimeConfigPanel";
import { AdapterRuntimeConfigPanel } from "@/components/jobs/AdapterRuntimeConfigPanel";
import { DescribeJobPanel } from "@/components/jobs/DescribeJobPanel";
import type { LodgeJobInput } from "@/components/jobs/describeJob";
import {
  CADENCE_PRESETS,
  type Cadence,
  cadenceDateConfig,
  describeCadence,
} from "@/components/jobs/schedulePresets";
import { useToast } from "@/hooks/use-toast";
import type { AdapterRecord as Adapter, SuburbRecord as Suburb } from "@/lib/api";
import {
  createQueueJob,
  fetchAdapters,
  fetchDistinctStatesForSuburbListIds,
  fetchSuburbLists,
  fetchSuburbs,
  runJob,
} from "@/lib/api";

interface CreateJobDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onJobCreated?: () => void;
}

const STATES = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];

const DEFAULT_JOB_FILTERS: AdapterRuntimeConfig = {
  listingType: "any",
  dateFilterMode: "relative",
  timeRange: "all",
  providerMode: "auto",
  provider: "any",
  providerPreference: ["firecrawl_scrape", "zyte", "direct"],
};

function normalizeDateFilterConfig(config: AdapterRuntimeConfig): AdapterRuntimeConfig {
  const mode =
    config.dateFilterMode || (config.dateFrom || config.dateTo ? "absolute" : "relative");
  if (mode === "absolute") {
    return {
      ...config,
      dateFilterMode: "absolute",
      timeRange: undefined,
    };
  }

  return {
    ...config,
    dateFilterMode: "relative",
    dateFrom: undefined,
    dateTo: undefined,
    timeRange: config.timeRange || "all",
  };
}

function getComputedWindow(config: AdapterRuntimeConfig): { from: string; to: string } | null {
  if ((config.dateFilterMode || "relative") !== "relative") return null;
  const timeRange = config.timeRange;
  if (!timeRange || timeRange === "all") return null;
  const days = TIME_RANGE_DAYS[timeRange];
  if (!days) return null;

  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - days);

  return {
    from: start.toISOString().slice(0, 10),
    to: end.toISOString().slice(0, 10),
  };
}

function hasInvalidAbsoluteDateRange(config: AdapterRuntimeConfig): boolean {
  if ((config.dateFilterMode || "relative") !== "absolute") return false;
  if (!config.dateFrom || !config.dateTo) return true;
  return new Date(config.dateFrom).getTime() > new Date(config.dateTo).getTime();
}

export const CreateJobDialog = ({ open, onOpenChange, onJobCreated }: CreateJobDialogProps) => {
  const { toast } = useToast();

  // Loading states
  const [isLoadingAdapters, setIsLoadingAdapters] = useState(true);
  const [isLoadingSuburbs, setIsLoadingSuburbs] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Data
  const [adapters, setAdapters] = useState<Adapter[]>([]);
  const [suburbLists, setSuburbLists] = useState<SuburbList[]>([]);
  const [suburbSearchResults, setSuburbSearchResults] = useState<Suburb[]>([]);

  // Form state
  const [jobName, setJobName] = useState("");
  const [runType, setRunType] = useState<"immediate" | "scheduled">("immediate");
  const [cronExpression, setCronExpression] = useState("0 0 * * *");
  const [cadence, setCadence] = useState<Cadence>("weekly");
  const effectiveCron = cadence === "custom" ? cronExpression : CADENCE_PRESETS[cadence].cron;

  // Suburb selection
  const [selectedSuburbs, setSelectedSuburbs] = useState<Suburb[]>([]);
  const [selectedSuburbListIds, setSelectedSuburbListIds] = useState<string[]>([]);
  /** Distinct states from selected suburb lists (loaded async). */
  const [listDerivedStates, setListDerivedStates] = useState<string[]>([]);
  const [listStatesLoading, setListStatesLoading] = useState(false);
  const [suburbSearch, setSuburbSearch] = useState("");
  const [suburbStateFilter, setSuburbStateFilter] = useState<string>("all");

  // Adapter selection + per-adapter configs
  const [selectedAdapterIds, setSelectedAdapterIds] = useState<string[]>([]);
  const [adapterConfigs, setAdapterConfigs] = useState<Record<string, Record<string, unknown>>>({});
  const [jobLevelFilters, setJobLevelFilters] = useState<AdapterRuntimeConfig>(DEFAULT_JOB_FILTERS);
  const [adapterTouched, setAdapterTouched] = useState<Record<string, boolean>>({});
  const [adapterAdvancedOpen, setAdapterAdvancedOpen] = useState<Record<string, boolean>>({});
  const [debugPauseOnSubmit, setDebugPauseOnSubmit] = useState(false);
  const [skipCompleteSales, setSkipCompleteSales] = useState(true);
  const [completeMissingData, setCompleteMissingData] = useState(true);

  // "Describe the job" (AI) is the default; the wizard is the Advanced fallback.
  const [mode, setMode] = useState<"describe" | "wizard">("describe");

  // Wizard step
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  useEffect(() => {
    if (!open) return;

    setJobName("");
    setSelectedSuburbs([]);
    setSelectedSuburbListIds([]);
    setListDerivedStates([]);
    setListStatesLoading(false);
    setSelectedAdapterIds([]);
    setAdapterConfigs({});
    setJobLevelFilters(DEFAULT_JOB_FILTERS);
    setAdapterTouched({});
    setAdapterAdvancedOpen({});
    setDebugPauseOnSubmit(false);
    setSkipCompleteSales(true);
    setCompleteMissingData(true);
    setCadence("weekly");
    setSuburbSearch("");
    setSuburbStateFilter("all");
    setSuburbSearchResults([]);
    setRunType("immediate");
    setCronExpression("0 0 * * *");
    setStep(1);
    setMode("describe");

    const loadActiveAdapters = async () => {
      try {
        setIsLoadingAdapters(true);
        const [adapterData, suburbListData] = await Promise.all([
          fetchAdapters(),
          fetchSuburbLists(),
        ]);
        setAdapters(adapterData.filter((adapter) => adapter.status === "active"));
        setSuburbLists(suburbListData);
      } catch (error) {
        console.error("Failed to load adapters:", error);
        toast({
          title: "Error",
          description: "Failed to load adapters",
          variant: "destructive",
        });
      } finally {
        setIsLoadingAdapters(false);
      }
    };

    void loadActiveAdapters();
  }, [open, toast]);

  // Search suburbs when search or filter changes
  useEffect(() => {
    if (suburbSearch.length < 2 && suburbStateFilter === "all") {
      setSuburbSearchResults([]);
      return;
    }

    const runSearch = async () => {
      try {
        setIsLoadingSuburbs(true);
        const { data } = await fetchSuburbs({
          state: suburbStateFilter !== "all" ? suburbStateFilter : undefined,
          search: suburbSearch || undefined,
          limit: 100,
        });

        const selectedIds = new Set(selectedSuburbs.map((suburb) => suburb.id));
        setSuburbSearchResults(data.filter((suburb) => !selectedIds.has(suburb.id)));
      } catch (error) {
        console.error("Failed to search suburbs:", error);
      } finally {
        setIsLoadingSuburbs(false);
      }
    };

    void runSearch();
  }, [suburbSearch, suburbStateFilter, selectedSuburbs]);

  useEffect(() => {
    if (selectedSuburbListIds.length === 0) {
      setListDerivedStates([]);
      setListStatesLoading(false);
      return;
    }

    let cancelled = false;
    setListStatesLoading(true);

    void fetchDistinctStatesForSuburbListIds(selectedSuburbListIds)
      .then((states) => {
        if (!cancelled) {
          setListDerivedStates(states);
          setListStatesLoading(false);
        }
      })
      .catch((error) => {
        console.error("Failed to resolve states for suburb lists:", error);
        if (!cancelled) {
          setListDerivedStates([]);
          setListStatesLoading(false);
          toast({
            title: "Error",
            description: "Could not load geography for suburb lists. Try again or re-select lists.",
            variant: "destructive",
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedSuburbListIds, toast]);

  const buildDefaultAdapterConfig = useCallback(
    (adapter: Adapter): Record<string, unknown> => {
      const baseConfig = normalizeDateFilterConfig({
        ...DEFAULT_JOB_FILTERS,
        ...(adapter.default_config || {}),
        ...jobLevelFilters,
      });

      const sharedDefaults = hasSharedAdapter(adapter.slug)
        ? getSharedAdapterDefaultConfig(adapter.slug)
        : {};

      return { ...sharedDefaults, ...baseConfig } as Record<string, unknown>;
    },
    [jobLevelFilters],
  );

  const geographyHasSelection = selectedSuburbs.length > 0 || selectedSuburbListIds.length > 0;
  const geographyReadyForAdapters = selectedSuburbListIds.length === 0 || !listStatesLoading;

  // States implied by manual suburbs plus suburb lists (when loaded)
  const selectedStates = useMemo(() => {
    const states = new Set<string>();
    for (const s of selectedSuburbs) states.add(s.state);
    if (geographyReadyForAdapters) {
      for (const st of listDerivedStates) states.add(st);
    }
    return states;
  }, [selectedSuburbs, listDerivedStates, geographyReadyForAdapters]);

  // Get compatible adapters based on selected geography (suburbs + lists)
  const compatibleAdapters = useMemo(() => {
    if (!geographyHasSelection) return adapters;

    if (!geographyReadyForAdapters) {
      if (selectedSuburbs.length > 0) {
        const manualOnly = new Set(selectedSuburbs.map((s) => s.state));
        return adapters.filter((adapter) => {
          if (!adapter.supported_states || adapter.supported_states.length === 0) return true;
          return [...manualOnly].some((state) => adapter.supported_states?.includes(state));
        });
      }
      return [];
    }

    if (selectedStates.size === 0) return [];

    return adapters.filter((adapter) => {
      if (!adapter.supported_states || adapter.supported_states.length === 0) {
        return true;
      }
      return [...selectedStates].some((state) => adapter.supported_states?.includes(state));
    });
  }, [adapters, geographyHasSelection, geographyReadyForAdapters, selectedSuburbs, selectedStates]);

  // Auto-select all compatible adapters when geography is ready and none picked yet
  useEffect(() => {
    if (
      geographyHasSelection &&
      geographyReadyForAdapters &&
      selectedAdapterIds.length === 0 &&
      compatibleAdapters.length > 0
    ) {
      const autoSelectedIds = compatibleAdapters.map((adapter) => adapter.id);
      setSelectedAdapterIds(autoSelectedIds);
      setAdapterConfigs((prevConfigs) => {
        const nextConfigs = { ...prevConfigs };
        for (const adapter of compatibleAdapters) {
          if (!nextConfigs[adapter.id]) {
            nextConfigs[adapter.id] = buildDefaultAdapterConfig(adapter);
          }
        }
        return nextConfigs;
      });
      setAdapterTouched((prev) => {
        const next = { ...prev };
        for (const adapter of compatibleAdapters) {
          if (next[adapter.id] === undefined) next[adapter.id] = false;
        }
        return next;
      });
      setAdapterAdvancedOpen((prev) => {
        const next = { ...prev };
        for (const adapter of compatibleAdapters) {
          if (next[adapter.id] === undefined) next[adapter.id] = false;
        }
        return next;
      });
    }
  }, [
    compatibleAdapters,
    geographyHasSelection,
    geographyReadyForAdapters,
    selectedAdapterIds.length,
    buildDefaultAdapterConfig,
  ]);

  useEffect(() => {
    if (selectedAdapterIds.length === 0) return;
    setAdapterConfigs((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const adapterId of selectedAdapterIds) {
        if (adapterTouched[adapterId]) continue;
        const adapter = adapters.find((item) => item.id === adapterId);
        if (!adapter) continue;
        next[adapterId] = buildDefaultAdapterConfig(adapter);
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [selectedAdapterIds, adapterTouched, adapters, buildDefaultAdapterConfig]);

  const addSuburb = (suburb: Suburb) => {
    setSelectedSuburbs((prev) => [...prev, suburb]);
    setSuburbSearchResults((prev) => prev.filter((s) => s.id !== suburb.id));
  };

  const removeSuburb = (suburbId: string) => {
    setSelectedSuburbs((prev) => prev.filter((s) => s.id !== suburbId));
  };

  const toggleAdapter = (adapterId: string) => {
    setSelectedAdapterIds((prev) => {
      if (prev.includes(adapterId)) {
        setAdapterConfigs((prevConfigs) => {
          const next = { ...prevConfigs };
          delete next[adapterId];
          return next;
        });
        setAdapterTouched((prevTouched) => {
          const next = { ...prevTouched };
          delete next[adapterId];
          return next;
        });
        setAdapterAdvancedOpen((prevOpen) => {
          const next = { ...prevOpen };
          delete next[adapterId];
          return next;
        });
        return prev.filter((id) => id !== adapterId);
      } else {
        const adapter = adapters.find((a) => a.id === adapterId);
        if (adapter) {
          setAdapterConfigs((prevConfigs) => ({
            ...prevConfigs,
            [adapterId]: buildDefaultAdapterConfig(adapter),
          }));
          setAdapterTouched((prevTouched) => ({ ...prevTouched, [adapterId]: false }));
          setAdapterAdvancedOpen((prevOpen) => ({ ...prevOpen, [adapterId]: false }));
        }
        return [...prev, adapterId];
      }
    });
  };

  const updateAdapterConfig = (
    adapterId: string,
    config: Record<string, unknown>,
    markTouched = true,
  ) => {
    setAdapterConfigs((prev) => ({ ...prev, [adapterId]: config }));
    if (markTouched) {
      setAdapterTouched((prev) => ({ ...prev, [adapterId]: true }));
    }
  };

  const getAdapterWarning = (adapterId: string): string | undefined => {
    const adapterConfig = adapterConfigs[adapterId] as AdapterRuntimeConfig | undefined;
    if (!adapterConfig) return undefined;
    const adapter = adapters.find((item) => item.id === adapterId);
    const globalListingType = jobLevelFilters.listingType || "any";
    const adapterListingType = adapterConfig.listingType || "any";
    if (globalListingType === "sold" && adapterListingType === "buy") {
      return "Global intent is sold history, but this adapter override is set to For Sale.";
    }
    if (
      jobLevelFilters.propertyTypes?.length === 1 &&
      adapterConfig.propertyTypes?.length === 1 &&
      jobLevelFilters.propertyTypes[0] !== adapterConfig.propertyTypes[0]
    ) {
      return "This adapter override uses a different property type than the global default.";
    }
    if (adapter?.slug === "realestate-com-au" && adapterListingType === "sold") {
      return "realestate.com.au does not expose URL-based sold date filters. Date windows are enforced after scrape.";
    }
    if (
      (jobLevelFilters.dateFilterMode || "relative") !==
      (adapterConfig.dateFilterMode || "relative")
    ) {
      return "This adapter uses a different date mode than the global defaults.";
    }
    return undefined;
  };

  const validateStep = (currentStep: number): string | null => {
    if (currentStep === 1) {
      if (selectedSuburbs.length === 0) {
        if (selectedSuburbListIds.length === 0) {
          return "Please select at least one suburb or suburb list";
        }
      }
    }
    if (currentStep === 2) {
      if (hasInvalidAbsoluteDateRange(jobLevelFilters)) {
        return "Global date settings are invalid. For absolute mode, set Date From and Date To (From must be before To).";
      }
      if (selectedSuburbListIds.length > 0 && listStatesLoading) {
        return "Still loading geography for suburb lists. Wait a moment, then try again.";
      }
    }
    if (currentStep === 3) {
      if (selectedSuburbListIds.length > 0 && listStatesLoading) {
        return "Still loading geography for suburb lists. Wait a moment, then try again.";
      }
      if (selectedAdapterIds.length === 0) {
        return "Please select at least one adapter";
      }
    }
    if (currentStep === 4) {
      if (!jobName.trim()) {
        return "Please enter a job name";
      }
      if (runType === "scheduled" && !effectiveCron.trim()) {
        return "Please enter a cron expression for scheduled jobs";
      }
    }
    return null;
  };

  const handleNext = () => {
    const error = validateStep(step);
    if (error) {
      toast({
        title: "Validation Error",
        description: error,
        variant: "destructive",
      });
      return;
    }
    setStep((prev) => Math.min(prev + 1, 4) as 1 | 2 | 3 | 4);
  };

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 1) as 1 | 2 | 3 | 4);
  };

  /** Create the job through createQueueJob and start it via runJob when immediate. */
  const lodgeJob = async (input: LodgeJobInput) => {
    try {
      setIsSubmitting(true);

      const job = await createQueueJob(
        input.name,
        input.suburbIds,
        input.adapterIds,
        input.suburbListIds,
        input.runType,
        input.runType === "scheduled" ? input.cronExpression : undefined,
        input.config,
        input.adapterConfigs,
        {
          initialStatus: input.debugPauseOnSubmit ? "paused" : undefined,
          debugPauseOnSubmit: input.debugPauseOnSubmit,
        },
      );

      toast({
        title: "Job Created",
        description: input.debugPauseOnSubmit
          ? `Job "${input.name}" created in paused debug mode. Start manually when ready.`
          : `Job "${input.name}" has been created with ${input.suburbIds.length} suburbs, ${input.suburbListIds.length} suburb lists, and ${input.adapterIds.length} adapters`,
      });

      // Reset form and close dialog
      resetForm();
      onOpenChange(false);
      onJobCreated?.();

      // If immediate, start the job in the background
      if (input.runType === "immediate" && !input.debugPauseOnSubmit) {
        toast({
          title: "Starting Job",
          description: "The job will begin discovery and scraping...",
        });

        runJob(job.id)
          .then((result) => {
            if (result.success) {
              toast({
                title: "Job Started",
                description: "Discovery phase has begun",
              });
            } else {
              toast({
                title: "Job Failed",
                description: result.error || "Unknown error",
                variant: "destructive",
              });
            }
          })
          .catch((err) => {
            console.error("Job run error:", err);
            toast({
              title: "Job Error",
              description: "Failed to start job",
              variant: "destructive",
            });
          });
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast({
        title: "Error",
        description: "Failed to create job",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const error = validateStep(4);
    if (error) {
      toast({
        title: "Validation Error",
        description: error,
        variant: "destructive",
      });
      return;
    }

    const normalizedGlobalConfig = normalizeDateFilterConfig(jobLevelFilters);
    const jobConfigPayload = {
      listingType:
        normalizedGlobalConfig.listingType && normalizedGlobalConfig.listingType !== "any"
          ? normalizedGlobalConfig.listingType
          : undefined,
      propertyTypes: normalizedGlobalConfig.propertyTypes?.length
        ? normalizedGlobalConfig.propertyTypes
        : undefined,
      dateFilterMode: normalizedGlobalConfig.dateFilterMode || "relative",
      timeRange:
        normalizedGlobalConfig.dateFilterMode !== "absolute" &&
        normalizedGlobalConfig.timeRange &&
        normalizedGlobalConfig.timeRange !== "all"
          ? normalizedGlobalConfig.timeRange
          : undefined,
      dateFrom:
        normalizedGlobalConfig.dateFilterMode === "absolute"
          ? normalizedGlobalConfig.dateFrom || undefined
          : undefined,
      dateTo:
        normalizedGlobalConfig.dateFilterMode === "absolute"
          ? normalizedGlobalConfig.dateTo || undefined
          : undefined,
      minPrice: normalizedGlobalConfig.minPrice || undefined,
      maxPrice: normalizedGlobalConfig.maxPrice || undefined,
      providerMode: normalizedGlobalConfig.providerMode || "auto",
      provider:
        normalizedGlobalConfig.provider && normalizedGlobalConfig.provider !== "any"
          ? normalizedGlobalConfig.provider
          : undefined,
      providerPreference: normalizedGlobalConfig.providerPreference || undefined,
      debugOptions: debugPauseOnSubmit ? { pauseOnSubmit: true } : undefined,
      crawlPolicy: { skipComplete: skipCompleteSales, completeMissing: completeMissingData },
      ...(runType === "scheduled" && cadence !== "custom" ? cadenceDateConfig(cadence) : {}),
    };

    const selectedAdapterConfigPayload = selectedAdapterIds.reduce<
      Record<string, Record<string, unknown>>
    >((acc, adapterId) => {
      const config = adapterConfigs[adapterId] as AdapterRuntimeConfig | undefined;
      if (config) {
        acc[adapterId] = normalizeDateFilterConfig(config) as Record<string, unknown>;
      }
      return acc;
    }, {});

    await lodgeJob({
      name: jobName,
      suburbIds: selectedSuburbs.map((s) => s.id),
      suburbListIds: selectedSuburbListIds,
      adapterIds: selectedAdapterIds,
      runType,
      cronExpression: effectiveCron,
      config: jobConfigPayload,
      adapterConfigs: selectedAdapterConfigPayload,
      debugPauseOnSubmit,
    });
  };

  const resetForm = () => {
    setJobName("");
    setSelectedSuburbs([]);
    setSelectedSuburbListIds([]);
    setListDerivedStates([]);
    setListStatesLoading(false);
    setSelectedAdapterIds([]);
    setAdapterConfigs({});
    setJobLevelFilters(DEFAULT_JOB_FILTERS);
    setAdapterTouched({});
    setAdapterAdvancedOpen({});
    setDebugPauseOnSubmit(false);
    setSkipCompleteSales(true);
    setCompleteMissingData(true);
    setSuburbSearch("");
    setSuburbStateFilter("all");
    setSuburbSearchResults([]);
    setRunType("immediate");
    setCronExpression("0 0 * * *");
    setStep(1);
    setMode("describe");
  };

  const toggleSuburbList = (listId: string) => {
    setSelectedSuburbListIds((prev) =>
      prev.includes(listId) ? prev.filter((id) => id !== listId) : [...prev, listId],
    );
  };

  const selectedSuburbLists = useMemo(
    () => suburbLists.filter((list) => selectedSuburbListIds.includes(list.id)),
    [suburbLists, selectedSuburbListIds],
  );

  const getStateBadgeColor = (state: string): string => {
    const colors: Record<string, string> = {
      VIC: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
      NSW: "bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-300",
      QLD: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
      WA: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
      SA: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
      TAS: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
      NT: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300",
      ACT: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
    };
    return colors[state] || "bg-gray-100 text-gray-800";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Scrape Job</DialogTitle>
          <DialogDescription>
            {mode === "describe"
              ? "Describe the job in plain English, check the preview, then confirm."
              : `Step ${step} of 4: ${
                  step === 1
                    ? "Select Suburbs"
                    : step === 2
                      ? "Global Config"
                      : step === 3
                        ? "Adapters + Overrides"
                        : "Run Settings"
                }`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant={mode === "describe" ? "default" : "outline"}
            onClick={() => setMode("describe")}
          >
            <Sparkles className="w-4 h-4 mr-1" />
            Describe the job
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === "wizard" ? "default" : "outline"}
            onClick={() => setMode("wizard")}
          >
            <SlidersHorizontal className="w-4 h-4 mr-1" />
            Advanced
          </Button>
        </div>

        {mode === "describe" && (
          <DescribeJobPanel
            isSubmitting={isSubmitting}
            onConfirm={(input) => void lodgeJob(input)}
            onCancel={() => onOpenChange(false)}
          />
        )}

        {/* Step Indicators */}
        {mode === "wizard" && (
          <div className="flex items-center justify-center gap-2 py-2">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  s === step
                    ? "bg-primary text-primary-foreground"
                    : s < step
                      ? "bg-primary/20 text-primary"
                      : "bg-muted text-muted-foreground"
                }`}
              >
                {s}
              </div>
            ))}
          </div>
        )}

        {mode !== "wizard" ? null : isLoadingAdapters ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Step 1: Suburb Selection */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="job-suburb-search"
                      name="jobSuburbSearch"
                      placeholder="Search suburbs..."
                      value={suburbSearch}
                      onChange={(e) => setSuburbSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Select value={suburbStateFilter} onValueChange={setSuburbStateFilter}>
                    <SelectTrigger className="w-[120px]">
                      <SelectValue placeholder="State" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All States</SelectItem>
                      {STATES.map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Search Results */}
                {(suburbSearch.length >= 2 || suburbStateFilter !== "all") && (
                  <div className="border rounded-lg">
                    <ScrollArea className="h-[200px]">
                      {isLoadingSuburbs ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                        </div>
                      ) : suburbSearchResults.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground text-sm">
                          No suburbs found
                        </div>
                      ) : (
                        <div className="p-2 space-y-1">
                          {suburbSearchResults.map((suburb) => (
                            <button
                              key={suburb.id}
                              type="button"
                              onClick={() => addSuburb(suburb)}
                              className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-muted text-left transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-muted-foreground" />
                                <span className="font-medium">{suburb.locality}</span>
                                <span className="text-muted-foreground text-sm">
                                  {suburb.postcode}
                                </span>
                              </div>
                              <Badge className={getStateBadgeColor(suburb.state)}>
                                {suburb.state}
                              </Badge>
                            </button>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </div>
                )}

                {/* Selected Suburbs */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Selected Suburbs ({selectedSuburbs.length})
                  </Label>
                  {selectedSuburbs.length === 0 ? (
                    <div className="border border-dashed rounded-lg p-4 text-center text-muted-foreground text-sm">
                      Search and select suburbs to scrape
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-muted/30">
                      {selectedSuburbs.map((suburb) => (
                        <Badge
                          key={suburb.id}
                          variant="secondary"
                          className="flex items-center gap-1 py-1"
                        >
                          {suburb.locality} {suburb.postcode}
                          <button
                            type="button"
                            onClick={() => removeSuburb(suburb.id)}
                            className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Suburb Lists */}
                <div className="space-y-2">
                  <Label>Suburb Lists ({selectedSuburbListIds.length})</Label>
                  {suburbLists.length === 0 ? (
                    <div className="border border-dashed rounded-lg p-4 text-center text-muted-foreground text-sm">
                      No saved suburb lists available.
                    </div>
                  ) : (
                    <div className="border rounded-lg p-2 space-y-1">
                      {suburbLists.map((list) => {
                        const isSelected = selectedSuburbListIds.includes(list.id);
                        return (
                          <div
                            key={list.id}
                            className="flex items-center justify-between gap-3 px-2 py-2 rounded-md hover:bg-muted/40 cursor-pointer"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <Checkbox
                                id={`suburb-list-${list.id}`}
                                checked={isSelected}
                                onCheckedChange={() => toggleSuburbList(list.id)}
                                aria-label={`Select suburb list ${list.name}`}
                              />
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{list.name}</p>
                                {list.description && (
                                  <p className="text-xs text-muted-foreground truncate">
                                    {list.description}
                                  </p>
                                )}
                              </div>
                            </div>
                            <Badge variant="outline" className="shrink-0 text-xs">
                              {list.item_count ?? 0}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Global Config */}
            {step === 2 && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Set global defaults for all adapters. Untouched adapters in the next step inherit
                  these values.
                </p>
                <AdapterRuntimeConfigPanel
                  adapterName="Global Defaults"
                  config={jobLevelFilters}
                  onChange={(config) => setJobLevelFilters(normalizeDateFilterConfig(config))}
                  isGlobal
                  showAdvanced
                  warningMessage={
                    hasInvalidAbsoluteDateRange(jobLevelFilters)
                      ? "Absolute mode requires a valid Date From and Date To range."
                      : undefined
                  }
                />
                {getComputedWindow(jobLevelFilters) && (
                  <p className="text-xs text-muted-foreground">
                    Rolling window preview: {getComputedWindow(jobLevelFilters)?.from} {"->"}{" "}
                    {getComputedWindow(jobLevelFilters)?.to}
                  </p>
                )}
              </div>
            )}

            {/* Step 3: Adapter Selection + Overrides */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  Select adapters and override only when needed. Untouched adapters remain aligned
                  to global defaults.
                </div>

                <div className="border rounded-lg divide-y">
                  {listStatesLoading &&
                  selectedSuburbListIds.length > 0 &&
                  selectedSuburbs.length === 0 ? (
                    <div className="p-6 flex flex-col items-center gap-2 text-muted-foreground">
                      <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
                      <span className="text-sm">Loading geography from suburb lists…</span>
                    </div>
                  ) : compatibleAdapters.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      No adapters available for the selected geography.
                    </div>
                  ) : (
                    compatibleAdapters.map((adapter) => {
                      const isSelected = selectedAdapterIds.includes(adapter.id);
                      const isTouched = adapterTouched[adapter.id] === true;
                      const showAdvanced = adapterAdvancedOpen[adapter.id] === true;
                      return (
                        <div key={adapter.id} className="border-b last:border-b-0">
                          <div className="flex items-center gap-3 p-4">
                            <Checkbox
                              id={`adapter-toggle-${adapter.id}`}
                              aria-label={`Enable adapter ${adapter.name}`}
                              checked={isSelected}
                              onCheckedChange={() => toggleAdapter(adapter.id)}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-2 font-medium hover:text-primary transition-colors"
                                  onClick={() => toggleAdapter(adapter.id)}
                                >
                                  <Plug className="w-4 h-4 text-muted-foreground" />
                                  <span>{adapter.name}</span>
                                </button>
                                {isSelected && (
                                  <Badge
                                    variant={isTouched ? "default" : "outline"}
                                    className="text-[10px]"
                                  >
                                    {isTouched ? "Override active" : "Using global defaults"}
                                  </Badge>
                                )}
                              </div>
                              {adapter.description && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  {adapter.description}
                                </p>
                              )}
                              {adapter.supported_states && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {adapter.supported_states.map((state) => (
                                    <Badge key={state} variant="outline" className="text-xs">
                                      {state}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          {isSelected && (
                            <div className="px-4 pb-4 pl-11 space-y-3">
                              {adapter.slug === "domain-com-au" && showAdvanced && (
                                <DomainConfigPanel
                                  config={
                                    (adapterConfigs[adapter.id] ||
                                      DOMAIN_DEFAULT_CONFIG) as DomainAdapterConfig
                                  }
                                  onChange={(config) =>
                                    updateAdapterConfig(
                                      adapter.id,
                                      normalizeDateFilterConfig(
                                        config as AdapterRuntimeConfig,
                                      ) as Record<string, unknown>,
                                    )
                                  }
                                />
                              )}
                              <AdapterRuntimeConfigPanel
                                adapterName={adapter.name}
                                config={
                                  (adapterConfigs[adapter.id] ||
                                    jobLevelFilters) as AdapterRuntimeConfig
                                }
                                onChange={(config) =>
                                  updateAdapterConfig(
                                    adapter.id,
                                    normalizeDateFilterConfig(config) as Record<string, unknown>,
                                  )
                                }
                                showAdvanced={showAdvanced}
                                onToggleAdvanced={() =>
                                  setAdapterAdvancedOpen((prev) => ({
                                    ...prev,
                                    [adapter.id]: !prev[adapter.id],
                                  }))
                                }
                                warningMessage={getAdapterWarning(adapter.id)}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="text-sm text-muted-foreground">
                  {selectedAdapterIds.length} adapter{selectedAdapterIds.length !== 1 ? "s" : ""}{" "}
                  selected
                </div>
              </div>
            )}

            {/* Step 4: Run Settings */}
            {step === 4 && (
              <div className="space-y-5">
                <div className="p-4 rounded-lg bg-muted/50 border space-y-3">
                  <h4 className="font-medium">Job Summary</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Suburbs:</span>
                      <span className="ml-2 font-medium">{selectedSuburbs.length}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Suburb Lists:</span>
                      <span className="ml-2 font-medium">{selectedSuburbListIds.length}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Adapters:</span>
                      <span className="ml-2 font-medium">{selectedAdapterIds.length}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {selectedSuburbs.slice(0, 5).map((s) => (
                      <Badge key={s.id} variant="outline" className="text-xs">
                        {s.locality}
                      </Badge>
                    ))}
                    {selectedSuburbs.length > 5 && (
                      <Badge variant="outline" className="text-xs">
                        +{selectedSuburbs.length - 5} more
                      </Badge>
                    )}
                  </div>
                  {selectedSuburbLists.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {selectedSuburbLists.slice(0, 3).map((list) => (
                        <Badge key={list.id} variant="secondary" className="text-xs">
                          {list.name}
                        </Badge>
                      ))}
                      {selectedSuburbLists.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{selectedSuburbLists.length - 3} more lists
                        </Badge>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="jobName">Job Name</Label>
                  <Input
                    id="jobName"
                    name="jobName"
                    placeholder="e.g., Melbourne Metro Properties"
                    value={jobName}
                    onChange={(e) => setJobName(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-3">
                  <Label>When to Run</Label>
                  <RadioGroup
                    value={runType}
                    onValueChange={(value) => {
                      const next = value as "immediate" | "scheduled";
                      setRunType(next);
                      if (next === "scheduled") {
                        setDebugPauseOnSubmit(false);
                      }
                    }}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="immediate" id="immediate" />
                      <Label
                        htmlFor="immediate"
                        className="flex items-center gap-2 cursor-pointer font-normal"
                      >
                        <Play className="w-4 h-4" />
                        Run Immediately
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="scheduled" id="scheduled" />
                      <Label
                        htmlFor="scheduled"
                        className="flex items-center gap-2 cursor-pointer font-normal"
                      >
                        <Calendar className="w-4 h-4" />
                        Schedule
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {runType === "scheduled" && (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="cadence">Cadence</Label>
                      <Select
                        value={cadence}
                        onValueChange={(value) => setCadence(value as Cadence)}
                      >
                        <SelectTrigger id="cadence">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">{CADENCE_PRESETS.weekly.label}</SelectItem>
                          <SelectItem value="monthly">{CADENCE_PRESETS.monthly.label}</SelectItem>
                          <SelectItem value="custom">Custom cron</SelectItem>
                        </SelectContent>
                      </Select>
                      {cadence !== "custom" && (
                        <p className="text-xs text-muted-foreground flex items-start gap-1">
                          <Clock className="w-3 h-3 mt-0.5 shrink-0" />
                          {describeCadence(cadence)} This replaces the time range set in step 2.
                        </p>
                      )}
                    </div>
                    {cadence === "custom" && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="cron">Cron Expression</Label>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-sm">
                                Standard cron format: minute hour day month weekday, in UTC (16:00
                                UTC is 2-3am Melbourne time)
                                <br />
                                <br />
                                Examples:
                                <br />
                                <code>0 0 * * *</code> - Daily at midnight UTC
                                <br />
                                <code>0 */6 * * *</code> - Every 6 hours
                                <br />
                                <code>0 9 * * 1-5</code> - Weekdays at 9am UTC
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <Input
                          id="cron"
                          name="cronExpression"
                          placeholder="0 0 * * *"
                          value={cronExpression}
                          onChange={(e) => setCronExpression(e.target.value)}
                          className="font-mono"
                        />
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Jobs will run automatically according to this schedule.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className="rounded-lg border p-4 bg-muted/20">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Label htmlFor="skip-complete-sales" className="text-sm font-medium">
                        Skip sales we already have
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Discovered pages whose sale we already hold completely (a price, a stored
                        page from a portal or agency, no fake-data flag) are not fetched again,
                        which saves provider credit. Turn off to refetch them on every run.
                        Property-profile sites (allhomes, property.com.au) are always refetched
                        because one page can hold a new sale.
                      </p>
                    </div>
                    <Switch
                      id="skip-complete-sales"
                      checked={skipCompleteSales}
                      onCheckedChange={setSkipCompleteSales}
                    />
                  </div>
                  <div className="flex items-start justify-between gap-4 mt-4 pt-4 border-t">
                    <div>
                      <Label htmlFor="complete-missing-data" className="text-sm font-medium">
                        Complete missing data
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Fetch pages whose sale we hold but incompletely (price only from SQM, no
                        stored page, or flagged as likely fake), unless the same site was fetched
                        for that page in the last 30 days. Turn off to skip every sale we already
                        hold. Photos count toward complete only when Settings → Crawl policy says
                        so.
                      </p>
                    </div>
                    <Switch
                      id="complete-missing-data"
                      checked={skipCompleteSales && completeMissingData}
                      disabled={!skipCompleteSales}
                      onCheckedChange={setCompleteMissingData}
                    />
                  </div>
                </div>

                <div className="rounded-lg border p-4 bg-muted/20">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Label htmlFor="debug-paused" className="text-sm font-medium">
                        Debug: create paused (do not start URL mapping)
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Creates the job only. No run, discovery tasks, or URL mapping starts until
                        you manually run it.
                      </p>
                      {runType !== "immediate" && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Available only for immediate jobs; scheduled jobs keep normal scheduling
                          behavior.
                        </p>
                      )}
                    </div>
                    <Switch
                      id="debug-paused"
                      checked={debugPauseOnSubmit}
                      disabled={runType !== "immediate"}
                      onCheckedChange={setDebugPauseOnSubmit}
                    />
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="flex justify-between sm:justify-between">
              <div>
                {step > 1 && (
                  <Button type="button" variant="outline" onClick={handleBack}>
                    Back
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                {step < 4 ? (
                  <Button type="button" onClick={handleNext}>
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : debugPauseOnSubmit ? (
                      "Create Paused Job"
                    ) : runType === "immediate" ? (
                      "Create & Run"
                    ) : (
                      "Create Job"
                    )}
                  </Button>
                )}
              </div>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
